import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';
import ErrorBanner from '../components/ErrorBanner';
import ZipCodeModal from '../components/ZipCodeModal';
import LocationPermissionModal from '../components/LocationPermissionModal';
import { getZipcodeFromLocation } from '../utils/geolocation';

export default function Marketplace() {
  const navigate = useNavigate();
  const { getToken, isSignedIn, isLoaded: authLoaded } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isCarouselPlaying, setIsCarouselPlaying] = useState(true);
  const hasLoadedZipCode = useRef(false);

  // Default to San Antonio, TX zipcode (78201)
  const DEFAULT_ZIPCODE = '78201';
  
  const [zipCode, setZipCode] = useState('');
  const [savedZipCode, setSavedZipCode] = useState(null);
  const [rememberZipCode, setRememberZipCode] = useState(false);
  const [showZipCodeModal, setShowZipCodeModal] = useState(false);
  const [showLocationPermissionModal, setShowLocationPermissionModal] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const loadCities = async (zip) => {
    if (!zip || zip.trim().length === 0) {
      setMarkets([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    // Safety: never leave loading true forever if the request hangs
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setError('Request took too long. Please try again.');
    }, 12000);

    try {
      const res = await api(`/api/cities/by-zipcode?zipCode=${encodeURIComponent(zip.trim())}`, {});
      const contentType = res.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');
      let data;
      try {
        data = isJson ? await res.json() : null;
      } catch (parseErr) {
        setError('Could not load locations. Please try again.');
        return;
      }
      if (!res.ok) {
        throw new Error(data?.error || `Error ${res.status}`);
      }
      setMarkets(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Could not load locations. Please try again.');
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  // Check geolocation permission status
  const checkGeolocationPermission = async () => {
    if (!navigator.geolocation) {
      return 'unsupported';
    }
    // Permissions API might not be available in all browsers
    if (!navigator.permissions || !navigator.permissions.query) {
      return 'prompt'; // Default to prompt if Permissions API not available
    }
    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      return result.state; // 'granted', 'denied', or 'prompt'
    } catch {
      return 'prompt'; // Default to prompt if query fails
    }
  };

  // Initialize zipcode and load cities - try geolocation first, then fallback
  useEffect(() => {
    if (hasLoadedZipCode.current) {
      return;
    }
    hasLoadedZipCode.current = true;

    const localZipCode = localStorage.getItem('garageHub_savedZipCode');
    const locationPermissionDenied = localStorage.getItem('garageHub_locationDenied') === 'true';
    
    // If we have a saved zipcode, use it
    if (localZipCode) {
      setZipCode(localZipCode);
      setSavedZipCode(localZipCode);
      loadCities(localZipCode);
      console.log(`default with zipcode ${localZipCode}`);
      
      // Still try to get from API if signed in (might be different)
      if (authLoaded && isSignedIn) {
        let cancelled = false;
        (async () => {
          try {
            const res = await api('/api/customers/me', { getToken });
            if (cancelled) return;
            if (res.ok) {
              const data = await res.json();
              if (data.savedZipCode && data.savedZipCode !== localZipCode) {
                setZipCode(data.savedZipCode);
                setSavedZipCode(data.savedZipCode);
                localStorage.setItem('garageHub_savedZipCode', data.savedZipCode);
                loadCities(data.savedZipCode);
              }
            }
          } catch (err) {
            console.error('Failed to load saved zipcode:', err);
          }
        })();
        return () => { cancelled = true; };
      }
      return;
    }

    // No saved zipcode - check permission status first
    (async () => {
      const permissionState = await checkGeolocationPermission();
      
      // If permission was previously denied, skip location modal and show zipcode modal
      if (locationPermissionDenied || permissionState === 'denied') {
        setZipCode(DEFAULT_ZIPCODE);
        loadCities(DEFAULT_ZIPCODE);
        setShowZipCodeModal(true);
        return;
      }
      
      // If permission is granted, try to get location automatically
      if (permissionState === 'granted') {
        setIsGettingLocation(true);
        try {
          const autoZipcode = await getZipcodeFromLocation();
          setIsGettingLocation(false);
          
          if (autoZipcode) {
            setZipCode(autoZipcode);
            setSavedZipCode(autoZipcode);
            localStorage.setItem('garageHub_savedZipCode', autoZipcode);
            loadCities(autoZipcode);
            
            // Save to API if signed in
            if (authLoaded && isSignedIn) {
              try {
                await api('/api/customers/me', {
                  getToken,
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ savedZipCode: autoZipcode }),
                });
              } catch (err) {
                console.error('Failed to save zipcode to API:', err);
              }
            }
            // Don't show zipcode modal - location was successfully obtained
            return;
          }
          
          // Zipcode lookup failed but location was obtained - show zipcode modal for manual entry
          setShowZipCodeModal(true);
          return;
        } catch (error) {
          console.error('Geolocation failed:', error);
          setIsGettingLocation(false);
          // Show zipcode modal if location failed (even if permission was granted)
          setShowZipCodeModal(true);
          return;
        }
      }
      
      // Permission is 'prompt' - show location permission modal
      setShowLocationPermissionModal(true);
      
      // Set default zipcode as fallback (will be used if user denies or geolocation fails)
      setZipCode(DEFAULT_ZIPCODE);
      loadCities(DEFAULT_ZIPCODE);
      console.log(`default with zipcode ${DEFAULT_ZIPCODE}`);
    })();
    
    // Also try to get from API if signed in (might have saved zipcode)
    if (authLoaded && isSignedIn) {
      let cancelled = false;
      (async () => {
        try {
          const res = await api('/api/customers/me', { getToken });
          if (cancelled) return;
          if (res.ok) {
            const data = await res.json();
            if (data.savedZipCode) {
              setZipCode(data.savedZipCode);
              setSavedZipCode(data.savedZipCode);
              localStorage.setItem('garageHub_savedZipCode', data.savedZipCode);
              loadCities(data.savedZipCode);
              setShowZipCodeModal(false); // Don't show popup if we got zipcode from API
              setShowLocationPermissionModal(false); // Don't show location modal if we got zipcode from API
            }
          }
        } catch (err) {
          console.error('Failed to load saved zipcode:', err);
        }
      })();
      return () => { cancelled = true; };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoaded, isSignedIn, getToken]);

  const handleZipCodeSubmit = async (e) => {
    e.preventDefault();
    const trimmed = zipCode.trim();
    if (!trimmed) {
      setError('Please enter a zipcode.');
      return;
    }

    // Save zipcode if "Remember" is checked
    if (rememberZipCode) {
      // Always save to localStorage as backup
      localStorage.setItem('garageHub_savedZipCode', trimmed);
      setSavedZipCode(trimmed);
      
      // Also save to API if user is signed in
      if (isSignedIn && authLoaded) {
        try {
          const res = await api('/api/customers/me', {
            getToken,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ savedZipCode: trimmed }),
          });
          if (res.ok) {
            // Already saved to localStorage above
          }
        } catch (err) {
          console.error('Failed to save zipcode to API:', err);
          // Still saved to localStorage, so it's OK
        }
      }
    }

    loadCities(trimmed);
  };

  const handleZipCodeModalSubmit = async (zip) => {
    setZipCode(zip);
    setSavedZipCode(zip);
    localStorage.setItem('garageHub_savedZipCode', zip);
    loadCities(zip);
    console.log(`obtained zipcode manually ${zip}`);
    
    // Also save to API if user is signed in
    if (isSignedIn && authLoaded) {
      try {
        await api('/api/customers/me', {
          getToken,
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ savedZipCode: zip }),
        });
      } catch (err) {
        console.error('Failed to save zipcode to API:', err);
      }
    }
  };

  const handleLocationAllow = async () => {
    setIsGettingLocation(true);
    setShowLocationPermissionModal(false);
    
    try {
      const autoZipcode = await getZipcodeFromLocation();
      setIsGettingLocation(false);
      
      if (autoZipcode) {
        // Successfully got zipcode from geolocation
        setZipCode(autoZipcode);
        setSavedZipCode(autoZipcode);
        localStorage.setItem('garageHub_savedZipCode', autoZipcode);
        localStorage.removeItem('garageHub_locationDenied'); // Clear denied flag since they allowed
        loadCities(autoZipcode);
        console.log(`user zipcode obtain auto with zipcode ${autoZipcode}`);
        
        // Also save to API if signed in
        if (authLoaded && isSignedIn) {
          try {
            await api('/api/customers/me', {
              getToken,
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ savedZipCode: autoZipcode }),
            });
          } catch (err) {
            console.error('Failed to save zipcode to API:', err);
          }
        }
        // Don't show zipcode modal - location was successfully obtained
        return;
      }
      
      // Zipcode lookup failed but location was obtained - show zipcode modal for manual entry
      localStorage.removeItem('garageHub_locationDenied'); // Clear denied flag since they allowed
      setShowZipCodeModal(true);
      return;
    } catch (error) {
      console.error('Geolocation failed:', error);
      setIsGettingLocation(false);
      
      // Only show zipcode modal if permission was explicitly denied
      if (error.errorType === 'permission_denied') {
        localStorage.setItem('garageHub_locationDenied', 'true');
        setShowZipCodeModal(true);
      } else {
        // For other errors (timeout, position unavailable, etc.), show zipcode modal
        // User already granted permission, but location failed - let them enter manually
        setShowZipCodeModal(true);
        localStorage.removeItem('garageHub_locationDenied'); // Clear denied flag since they allowed
      }
    }
  };

  const handleLocationDeny = () => {
    // User denied location - mark as denied and show zipcode entry popup
    localStorage.setItem('garageHub_locationDenied', 'true');
    setShowZipCodeModal(true);
  };

  const handleMarketClick = (cityId) => {
    navigate(`/city/${cityId}`);
  };

  // Auto-play carousel for hero banner
  useEffect(() => {
    if (isCarouselPlaying && markets.length > 0) {
      const interval = setInterval(() => {
        setCarouselIndex((prev) => (prev + 1) % markets.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isCarouselPlaying, markets.length]);

  const handleCityClick = (cityId) => {
    navigate(`/city/${cityId}`);
  };

  // Generate different background colors based on index
  const getHeroBackground = (index) => {
    const colors = [
      'from-pink-200 to-pink-300', // Pink
      'from-blue-200 to-blue-300', // Blue
      'from-purple-200 to-purple-300', // Purple
      'from-orange-200 to-orange-300', // Orange
      'from-green-200 to-green-300', // Green
      'from-indigo-200 to-indigo-300', // Indigo
      'from-teal-200 to-teal-300', // Teal
      'from-rose-200 to-rose-300', // Rose
    ];
    return colors[index % colors.length];
  };

  // Amazon-style home layout
  const renderAmazonDesign = () => (
    <div className="flex flex-col bg-white -mx-4 md:-mx-8 -mt-4">
      {/* Hero Banner - Amazon Style with Pink Background */}
      <div className="bg-[#F8D7DA] -mx-4 md:-mx-8 px-4 md:px-8 py-6 mb-4 relative overflow-hidden">
        <div className="max-w-screen-md mx-auto">
          <p className="text-sm font-medium text-slate-700 mb-1">Discover Today</p>
          <h1 className="text-3xl font-bold text-slate-900 mb-4">
            Find garage sales near you
          </h1>
          {markets.length > 0 && (
            <div className="relative h-48 rounded-lg overflow-hidden">
              <div 
                key={carouselIndex}
                className={`absolute inset-0 bg-gradient-to-r ${getHeroBackground(carouselIndex)} rounded-lg cursor-pointer transition-all duration-500 ease-in-out animate-slide-in-left`}
                onClick={() => handleMarketClick(markets[carouselIndex % markets.length].id)}
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-6xl mb-2">🛍️</div>
                    <p className="text-lg font-semibold text-slate-800">{markets[carouselIndex % markets.length]?.name ? `${markets[carouselIndex % markets.length].name} Garage sale` : 'Discover Garage Sales'}</p>
                    <p className="text-sm text-slate-600 mt-1">Click to explore →</p>
                  </div>
                </div>
                {/* Decorative elements */}
                <div className="absolute top-4 left-4 w-16 h-16 bg-white/30 rounded-full"></div>
                <div className="absolute bottom-4 right-4 w-20 h-20 bg-white/30 rounded-full"></div>
              </div>
            </div>
          )}
          {markets.length === 0 && (
            <div className="relative h-48 bg-gradient-to-r from-pink-200 to-pink-300 rounded-lg overflow-hidden">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-6xl mb-2">🎁</div>
                  <p className="text-lg font-semibold text-slate-800">Discover Garage Sales</p>
                </div>
              </div>
              <div className="absolute top-4 left-4 w-16 h-16 bg-white/30 rounded-full"></div>
              <div className="absolute bottom-4 right-4 w-20 h-20 bg-white/30 rounded-full"></div>
            </div>
          )}
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      {markets.length === 0 ? (
        <div className="bg-white rounded-lg p-8 text-center border border-slate-200 mx-4">
          <p className="text-lg text-slate-600 mb-3">
            No garage sales available at this time.
          </p>
          <button
            onClick={() => setShowZipCodeModal(true)}
            className="text-indigo-600 hover:text-indigo-700 hover:underline font-medium"
          >
            Check another zipcode?
          </button>
        </div>
      ) : (
        <div className="px-4 space-y-4">
          {/* Product Grid - Amazon Style */}
          <div className="grid grid-cols-2 gap-3">
            {markets.map((market) => (
              <div
                key={market.id}
                onClick={() => handleMarketClick(market.id)}
                className="bg-white border border-slate-200 rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="aspect-square w-full bg-orange-100 overflow-hidden flex items-center justify-center">
                  <img
                    src="/icons8-online-store-100.png"
                    alt={market.name}
                    className="w-full h-full object-contain p-4"
                  />
                </div>
                <div className="p-3">
                  <h2 className="text-sm font-semibold text-slate-900 mb-1 line-clamp-2 min-h-[2.5rem] text-center">
                    {market.name} Garage sale
                  </h2>
                  {market.address && (
                    <p className="text-xs text-slate-600 line-clamp-1 mb-1">
                      📍 {market.address.split(',')[0]}
                    </p>
                  )}
                  <div className="mt-2 pt-2 border-t border-slate-100">
                    <span className="text-xs font-medium text-orange-600 block text-center">
                      Shop now →
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {renderAmazonDesign()}
      <LocationPermissionModal
        isOpen={showLocationPermissionModal}
        onClose={() => setShowLocationPermissionModal(false)}
        onAllow={handleLocationAllow}
        onDeny={handleLocationDeny}
      />
      <ZipCodeModal
        isOpen={showZipCodeModal}
        onClose={() => {
          setShowZipCodeModal(false);
          // If user closes modal without entering zipcode, use default zipcode
          if (!zipCode || zipCode === DEFAULT_ZIPCODE) {
            setZipCode(DEFAULT_ZIPCODE);
            loadCities(DEFAULT_ZIPCODE);
          }
        }}
        onSubmit={handleZipCodeModalSubmit}
        initialZipCode={zipCode}
      />
      {isGettingLocation && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/30">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 space-y-4 max-w-sm">
            <div className="flex items-center gap-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Getting your location...
              </p>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Please allow location access in your browser to find garage sales near you. This may take a moment.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
